// src/components/Brand/EbiosensLogo.jsx — EBIOSENS mark (C-arc + central circle)
// Dark variant optimized for #080E1F canvas ; Light variant keeps original dark gradient for white backgrounds
import React from 'react';
import PropTypes from 'prop-types';

export default function EbiosensLogo({ size = 32, variant = 'dark', withWordmark = true, wordmarkClassName = '', className = '', title = 'EBIOSENS' }) {
  const id = React.useId();
  const arcId = `arc-${id}`;
  const circleId = `circle-${id}`;
  const isDark = variant === 'dark';

  // Dark bg needs light blue gradients for contrast; Light keeps provided SVG colors
  const arcStops = isDark
    ? [
        { offset: '0%', color: '#5a8bff' },
        { offset: '35%', color: '#2e6bff' },
        { offset: '68%', color: '#3ba8ff' },
        { offset: '100%', color: '#c7d7ff' },
      ]
    : [
        { offset: '5%', color: '#111042' },
        { offset: '15%', color: '#001d5c' },
        { offset: '55%', color: '#0093c7' },
        { offset: '80%', color: '#0264ff' },
        { offset: '100%', color: '#0366ff' },
      ];

  const circleStops = isDark
    ? [
        { offset: '0%', color: '#2a5bff' },
        { offset: '100%', color: '#8ec1ff' },
      ]
    : [
        { offset: '0%', color: '#0010ff' },
        { offset: '100%', color: '#106fff' },
      ];

  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label={title} role="img">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 600 600"
        width={size}
        height={size}
        aria-hidden="true"
        className="shrink-0"
      >
        <defs>
          <linearGradient id={arcId} x1="15%" y1="85%" x2="85%" y2="15%">
            {arcStops.map((s) => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
          <linearGradient id={circleId} x1="20%" y1="80%" x2="80%" y2="20%">
            {circleStops.map((s) => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
        </defs>
        <path
          d="M 476 300 A 176 176 0 1 1 424.45 175.55 L 384.85 215.15 A 120 120 0 1 0 420 300 Z"
          fill={`url(#${arcId})`}
        />
        <circle cx="300" cy="300" r="72" fill={`url(#${circleId})`} />
      </svg>
      {withWordmark && (
        <span className={`font-extrabold tracking-tight leading-none select-none ${wordmarkClassName}`} style={{ fontSize: size * 0.72 }}>
          <span className={isDark ? 'text-white' : 'text-slate-900'}>EBIOS</span>
          <span className="text-blue-500">ENS</span>
        </span>
      )}
      <span className="sr-only">{title}</span>
    </span>
  );
}

EbiosensLogo.propTypes = {
  size: PropTypes.number,
  variant: PropTypes.oneOf(['dark', 'light']),
  withWordmark: PropTypes.bool,
  wordmarkClassName: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
};
